import { action, computed } from 'mobx';
import { ISignaturesBlockResponse } from '~/types/server.types';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import type { SignatureBlock } from '~/models/blocks/signature-block.model';
import { ContractBlockErrors } from '~/types/error.types';
import { TBlock } from '~/types/builder.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { partition } from 'lodash';

type SignaturesBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    Pick<ISignaturesBlockResponse, 'type'>;

export class SignaturesBlock extends ContainerableBlock {
    type: 'signatures_block';

    constructor(
        block: ISignaturesBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    get isDeletable() {
        return false;
    }

    @computed
    get missingClientSignature() {
        return this.clientsSignatureBlocks.length === 0;
    }

    @computed
    get clientsSignatureBlocks(): Array<SignatureBlock> {
        return this.childBlocks.filter(
            (child): child is SignatureBlock =>
                (child as SignatureBlock).signature.signee_type === 'client'
        );
    }

    @computed
    get missingOwnerSignature() {
        return this.vendorsSignatureBlocks.length === 0;
    }

    @computed
    get vendorsSignatureBlocks(): Array<SignatureBlock> {
        return this.childBlocks.filter(
            (child): child is SignatureBlock =>
                (child as SignatureBlock).signature.signee_type === 'vendor'
        );
    }

    @computed
    get hasClientSignatures() {
        const childBlocks = this.childBlocks as SignatureBlock[];
        return childBlocks
            .filter(child => child.signature.signee_type === 'client')
            .some(child => child.isSigned);
    }

    @computed
    get isContractCompleted() {
        return this.childBlocks.every(
            child => (child as SignatureBlock).isSigned
        );
    }

    @computed
    get numberOfSignatures() {
        return this.childBlocks.length;
    }

    @computed
    get numberOfSigned() {
        return this.childBlocks.filter(
            child => (child as SignatureBlock).isSigned
        ).length;
    }

    @computed
    get isContractInProgress() {
        return this.numberOfSigned > 0 && !this.isContractCompleted;
    }

    @computed
    get shouldCounterSign() {
        const childBlocks = this.childBlocks as SignatureBlock[];
        return childBlocks
            .filter(child => child.signature.signee_type === 'client')
            .every(child => child.isSigned);
    }

    @computed
    get hasCriticalChanges() {
        return (this.childBlocks as SignatureBlock[]).some(
            signatureBlock => signatureBlock.hasCriticalChanges
        );
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors, selectedFlow }
        } = this.rootStore;

        if (selectedFlow.is_template || !showErrors) {
            return {};
        }

        const errors: any = {};

        if (this.missingOwnerSignature || this.missingClientSignature) {
            errors.hasErrors = true;
        }

        return errors;
    }

    @computed
    get blockInputValidations() {
        const validations = {};
        const unassignedSignatures = this.childBlocks.filter(child => {
            return !(child as SignatureBlock).signature.signee_id;
        }).length;

        if (unassignedSignatures) {
            validations[ContractBlockErrors.UnassignedSignature] =
                unassignedSignatures;
        }

        if (this.missingOwnerSignature || this.missingClientSignature) {
            validations[ContractBlockErrors.MissingSignature] = 1;
        }

        return validations;
    }

    @computed get assignedSignaturesMemberIds() {
        return this.childBlocks
            .map(signatureBlock => {
                return (signatureBlock as SignatureBlock).signature.signee_id;
            })
            .filter(Boolean);
    }

    @computed
    get assignedSignaturesMembers() {
        return this.childBlocks
            .map(signatureBlock => {
                return (signatureBlock as SignatureBlock).getDataForServer();
            })
            .filter(Boolean);
    }

    isDraggable() {
        return false;
    }

    dndAcceptedTypes() {
        return null;
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    getDataForServer(): SignaturesBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'signatures_block'
        };
    }

    canBeParentOf(block: TBlock) {
        return block.type === 'signature_block';
    }

    @action
    onBlockCreated() {
        this.populateSignatures();
    }

    @action
    populateSignatures() {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        const [flowVendors, flowClients] = partition(
            selectedFlow?.members || [],
            m => m.system_user_type === 'vendor'
        );

        if (flowVendors.length === 0) {
            flowVendors.push({
                _id: '',
                full_name: '',
                email: ''
            });
        }

        const remainingVendors = [...flowVendors];
        const remainingClients = [...flowClients];

        (this.childBlocks as SignatureBlock[]).forEach(signatureBlock => {
            if (!signatureBlock.signature) {
                return;
            }

            if (signatureBlock.signature.signee_type === 'vendor') {
                const vendor = remainingVendors.shift();
                signatureBlock.setSignee(vendor);
            } else if (signatureBlock.signature.signee_type === 'client') {
                const client =
                    remainingClients.length > 0
                        ? remainingClients.shift()
                        : null;
                signatureBlock.setSignee(client);
            } else {
                signatureBlock.setSignee(null);
            }
        });
    }
}
